'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@workspace/ui/components/avatar';
import { Stats } from './Stats';
import { TagsList } from './TagsList';
import { cn } from '@workspace/ui/lib/utils';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import PageLoader from '@/src/components/ui/PageLoader';
import { PartnerCheckmark } from '../../settings/components/PartnerBadge';
import Image from 'next/image';

type Props = {
  tags: string[];
  authorName: string;
  authorAvatar?: string;
  isPartner?: boolean;
  createdAt: string;
  title: string;
  likes: number;
  comments: number;
  image: string;
  authorLink: string;
  id: number;
  isBitsExplore?: boolean;
  slug?: string;
  visibility: string;
  isMobileView?: boolean;
  layout?: 'grid' | 'carousel';
};

export const BitCard = ({
  tags,
  authorName,
  isPartner = false,
  authorAvatar,
  createdAt,
  title,
  likes,
  comments,
  image,
  authorLink,
  id,
  isBitsExplore,
  slug,
  visibility,
  isMobileView,
  layout = 'grid',
}: Props) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const isCarousel = layout === 'carousel';

  const handleCardClick = () => {
    setIsLoading(true);
    router.push(`/bit/${slug}`);
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <div
        onClick={handleCardClick}
        className={cn(
          'bg-bg-gray rounded-3xl p-2 hover:shadow-lg hover:bg-bg-dark-gray hover:cursor-pointer transition-all ease-in-out duration-300 w-[270px] h-[280px] text-black',
          !isCarousel && 'w-[270px] h-[280px] mx-2',
          !isCarousel && {
            'md:w-[330px]': isBitsExplore && !isMobileView,
            'xl:w-[400px] md:h-[340px] md:mx-0 w-full h-[320px]': !isMobileView,
            'w-[270px] h-[280px] flex': isBitsExplore && isMobileView,
          },
          isCarousel &&
            'shrink-0 w-[270px] h-[280px] md:w-[330px] xl:w-[350px] md:h-[300px]'
        )}
      >
        <div className="flex flex-col h-full justify-between pb-1">
          <div
            className={cn(
              'relative w-full h-2/3 rounded-2xl overflow-hidden mb-2',
              {
                'md:h-[230px]': !isMobileView,
                'h-[200px]': isMobileView,
              }
            )}
          >
            <Image
              src={image}
              alt="Bit Image"
              className="w-full h-full object-cover"
              loading="lazy"
              priority={false}
              fill
              placeholder="blur"
              blurDataURL={image}
              sizes="(max-width: 768px) 100vw, 400px"
            />
            <TagsList
              tags={tags}
              className={cn('absolute -top-1 left-1.5 flex', {
                'md:top-1 md:left-3 md:gap-1.5': !isMobileView,
              })}
            />
          </div>
          <div
            className={cn(
              'flex items-end gap-2 -mt-6 pl-4 mb-1 md:mb-2 md:-mt-5'
            )}
          >
            <Avatar
              className={cn(
                'size-6 border-4 border-background-gray bg-background-gray',
                { 'md:size-8': !isMobileView }
              )}
            >
              <AvatarImage
                src={authorAvatar || '/images/default-avatar.png'}
                alt="profile image"
              />
              <AvatarFallback>{authorName.charAt(0)}</AvatarFallback>
            </Avatar>
            <span
              className={cn(
                'font-medium text-xs hover:underline hover:cursor-pointer flex items-center',
                { 'md:text-sm': !isMobileView }
              )}
              onClick={(e) => {
                e.stopPropagation();
                setIsLoading(true);
                window.location.href = `/${authorLink}`;
              }}
            >
              {authorName}
              {isPartner && (
                <PartnerCheckmark className="inline-block size-3" />
              )}
            </span>
          </div>
          <h2 className="font-medium text-lg p-2 pb-0">{title}</h2>
          <Stats
            likes={likes}
            comments={comments}
            createdAt={createdAt}
            className="text-black/60"
            isPrivateBit={visibility === 'private'}
          />
        </div>
      </div>
    </>
  );
};
